export type BydaPointDraft = {
  longitude: string;
  latitude: string;
};

export type BydaProjectSeed = {
  projectId?: number | string | null;
  ProjectId?: number | string | null;
  projectCode?: string | null;
  ProjectCode?: string | null;
  siteAddress?: string | null;
  SiteAddress?: string | null;
  suburb?: string | null;
  Suburb?: string | null;
  postCode?: string | number | null;
  PostCode?: string | number | null;
  Postcode?: string | number | null;
};

export type BydaDraftForm = {
  userReference: string;
  digStartAt: string;
  digEndAt: string;
  activityTypes: string[];
  workplaceSelections: string[];
  authorityId: string;
  notes: string;
  isPlanningJob: boolean;
  isSandboxTest: boolean;
  userTimezone: string;
  addressLine1: string;
  locality: string;
  postcode: string;
  state: string;
  country: string;
  points: BydaPointDraft[];
};

export function toInputString(value: unknown): string {
  if (value === null || value === undefined) return "";
  return String(value);
}

function formatDateInput(value: Date): string {
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, "0");
  const day = `${value.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function addDays(value: Date, days: number): Date {
  const next = new Date(value);
  next.setDate(next.getDate() + days);
  return next;
}

export function createEmptyBydaPointDraft(): BydaPointDraft {
  return {
    longitude: "",
    latitude: "",
  };
}

export function createBydaDraftFromProject(project: BydaProjectSeed): BydaDraftForm {
  const today = new Date();
  return {
    userReference: toInputString(project?.projectCode || project?.ProjectCode || project?.projectId || project?.ProjectId),
    digStartAt: formatDateInput(today),
    digEndAt: formatDateInput(addDays(today, 6)),
    activityTypes: [],
    workplaceSelections: [...DEFAULT_BYDA_WORKPLACE_SELECTIONS],
    authorityId: "",
    notes: "",
    isPlanningJob: true,
    isSandboxTest: false,
    userTimezone: "Australia/Melbourne",
    addressLine1: toInputString(project?.siteAddress || project?.SiteAddress),
    locality: toInputString(project?.suburb || project?.Suburb),
    postcode: toInputString(project?.postCode ?? project?.PostCode ?? project?.Postcode),
    state: "VIC",
    country: "AUS",
    points: [
      createEmptyBydaPointDraft(),
      createEmptyBydaPointDraft(),
      createEmptyBydaPointDraft(),
      createEmptyBydaPointDraft(),
    ],
  };
}

export function parseCsvTokens(value: string): string[] {
  return Array.from(
    new Set(
      String(value || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
    )
  );
}

export type BydaOptionCode = {
  code: string;
  label?: string | null;
};

export const BYDA_WORKPLACE_OPTIONS = [
  { code: "Road", label: "Road" },
  { code: "Footpath", label: "Footpath" },
  { code: "Nature Strip", label: "Nature strip" },
  { code: "Private Property", label: "Private property" },
] as const;

export type BydaWorkplaceOptionCode = typeof BYDA_WORKPLACE_OPTIONS[number]["code"];

export const DEFAULT_BYDA_WORKPLACE_SELECTIONS: BydaWorkplaceOptionCode[] = [
  "Footpath",
  "Private Property",
];

export function toggleBydaCode(current: string[], code: string, enabled: boolean): string[] {
  const filtered = current.filter((value) => value !== code);
  if (!enabled) {
    return filtered;
  }
  return [...filtered, code];
}

function normalizeBydaActivityText(value: unknown): string {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

export function getDefaultBydaDesignActivityCodes(options: BydaOptionCode[]): string[] {
  const getOptionText = (option: BydaOptionCode) => [
    normalizeBydaActivityText(option.label),
    normalizeBydaActivityText(option.code),
  ].filter(Boolean).join(" ");

  const planningAndDesign = options.find((option) => {
    const text = getOptionText(option);
    return text.includes("planning") && text.includes("design");
  });
  if (planningAndDesign?.code) {
    return [planningAndDesign.code];
  }

  const designOnly = options.find((option) => {
    const text = getOptionText(option);
    return text.includes("design") && !text.includes("conveyancing");
  });
  return designOnly?.code ? [designOnly.code] : [];
}

export function syncBydaCodes(
  current: string[],
  options: BydaOptionCode[],
  config?: {
    fallback?: string[];
    allowEmpty?: boolean;
  }
): string[] {
  const allowed = new Set(options.map((option) => option.code).filter(Boolean));
  const kept = Array.from(new Set(current.filter((value) => allowed.has(value))));
  if (kept.length > 0) {
    return kept;
  }

  const fallback = Array.from(new Set((config?.fallback || []).filter((value) => allowed.has(value))));
  if (fallback.length > 0) {
    return fallback;
  }

  if (config?.allowEmpty) {
    return [];
  }

  const first = options.find((option) => allowed.has(option.code));
  return first?.code ? [first.code] : [];
}

export function mapWorkplaceSelectionsToPayload(workplaceSelections: string[]): {
  locationTypes: string[];
  locationsInRoad: string[];
} {
  const uniqueSelections = Array.from(new Set((workplaceSelections || []).filter(Boolean)));
  const roadSelections = uniqueSelections.filter((value) => ["Road", "Footpath", "Nature Strip"].includes(value));
  const hasPrivateProperty = uniqueSelections.includes("Private Property");

  return {
    locationTypes: [
      ...(hasPrivateProperty ? ["Private"] : []),
      ...(roadSelections.length > 0 ? ["Road Reserve"] : []),
    ],
    locationsInRoad: roadSelections,
  };
}

export function tryBuildPolygonFromDraftPoints(points: BydaPointDraft[]) {
  try {
    return buildBydaDraftPolygon(points);
  } catch {
    return null;
  }
}

export function buildBydaDraftPolygon(points: BydaPointDraft[]) {
  const vertices = points.flatMap((point, index) =>
    point.longitude.trim() || point.latitude.trim() ? [parseBydaPoint(point, index)] : []
  );
  return buildPolygonFromPoints(vertices);
}

function parseBydaPoint(value: BydaPointDraft, index: number): [number, number] {
  const longitude = Number(value.longitude);
  const latitude = Number(value.latitude);
  if (!value.longitude.trim() || !value.latitude.trim() || !Number.isFinite(longitude) || !Number.isFinite(latitude)) {
    throw new Error(`Point ${index + 1} needs both longitude and latitude.`);
  }
  if (longitude <= 0 || longitude > 180 || latitude >= 0 || latitude < -90) {
    throw new Error(`Point ${index + 1} must be an Australian site coordinate in longitude, latitude order.`);
  }
  return [longitude, latitude];
}

export function buildPolygonFromPoints(points: [number, number][]) {
  if (new Set(points.map(([lon, lat]) => `${lon},${lat}`)).size < 3) {
    throw new Error("Mark at least three distinct points around the work site on the map.");
  }

  const closedPoints = [...points];
  const first = closedPoints[0];
  const last = closedPoints[closedPoints.length - 1];
  if (first[0] !== last[0] || first[1] !== last[1]) {
    closedPoints.push([first[0], first[1]]);
  }

  const longitudes = closedPoints.map((point) => point[0]);
  const latitudes = closedPoints.map((point) => point[1]);

  return {
    type: "Polygon" as const,
    coordinates: [closedPoints],
    bbox: [
      Math.min(...longitudes),
      Math.min(...latitudes),
      Math.max(...longitudes),
      Math.max(...latitudes),
    ],
  };
}

export function normalizeDraftField(value: string): string | null {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}
